// AR projection maths (F7, 2026-10-02). A line-for-line mirror of
// src/wcc_viewer/field_ar.py, which holds the unit tests
// (tests/test_field_ar_offline.py, tests/test_field_ar_fixes.py) - change
// both together.
//
// The SERVER does the asset work (which assets, ENU conversion,
// uncertainty, the calibration offset). The phone only needs what must
// run at display rate: the camera basis from the live compass/tilt and
// the projection of ground points to the screen.
//
// Conventions: ENU metres (east, north, up) from the device fix; assets
// drawn ON THE GROUND (u = 0); the camera CAMERA_HEIGHT_M above it; W3C
// DeviceOrientation beta/gamma; screen x right, y down, CSS pixels.

export const CAMERA_HEIGHT_M = 1.4;
export const NEAR_PLANE_M = 0.5;
export const DEFAULT_COMPASS_ERR_DEG = 10;
export const MIN_COMPASS_ERR_DEG = 3;
export const CALIBRATION_MAX_HEADING_DEG = 45;
// What is drawn over the camera (field_ar.py: the server sends the same
// values in ArAssets.draw, which win when present). A ground point far away
// sits on the horizon line and a 1 deg pitch error moves it metres, so
// drawing stops where the ray is MIN_DEPRESSION_DEG below the horizon.
export const AR_DRAW_MAX_M = 25;
export const AR_FADE_START_M = 12;
export const MIN_DEPRESSION_DEG = 4;
export const BAND_MAX_M = 6;
export const CAL_RESIDUAL_GPS_M = 2;

export type Vec3 = [number, number, number];

export interface CameraBasis {
  right: Vec3;
  up: Vec3;
  fwd: Vec3;
  pitch: number;
  roll: number;
  heading: number;
}

const D2R = Math.PI / 180;

function norm360(d: number): number {
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

function angleDiff(a: number, b: number): number {
  const d = norm360(a - b);
  return d > 180 ? d - 360 : d;
}

export function enuBearing(e: number, n: number): number {
  return norm360(Math.atan2(e, n) / D2R);
}

/** Small-offset ENU between two fixes (the phone's own movement since the
 * server's ENU origin; tens of metres, where equirectangular is exact to
 * millimetres). */
export function localOffset(lat0: number, lon0: number, lat: number, lon: number): [number, number] {
  const la = lat0 * D2R;
  const a = 6378137, e2 = 0.00669437999014;
  const s = Math.sin(la);
  const nRad = a / Math.sqrt(1 - e2 * s * s);
  const mRad = (a * (1 - e2)) / Math.pow(1 - e2 * s * s, 1.5);
  return [(lon - lon0) * D2R * nRad * Math.cos(la), (lat - lat0) * D2R * mRad];
}

function rotZ(v: Vec3, deg: number): Vec3 {
  const r = deg * D2R;
  const c = Math.cos(r), s = Math.sin(r);
  return [v[0] * c + v[1] * s, -v[0] * s + v[1] * c, v[2]];
}

/** Camera frame in ENU from the TRUE camera heading, W3C beta/gamma and
 * screen.orientation.angle. null when the camera looks straight up/down. */
export function cameraBasis(heading: number, beta: number, gamma: number, screenAngle = 0): CameraBasis | null {
  const b = beta * D2R, g = gamma * D2R, a = screenAngle * D2R;
  const cb = Math.cos(b), sb = Math.sin(b), cg = Math.cos(g), sg = Math.sin(g);
  const rot = (v: Vec3): Vec3 => [cg * v[0] + sg * v[2], sb * sg * v[0] + cb * v[1] - sb * cg * v[2], -cb * sg * v[0] + sb * v[1] + cb * cg * v[2]];
  let fwd = rot([0, 0, -1]);
  let up = rot([Math.sin(a), Math.cos(a), 0]);
  let right = rot([Math.cos(a), -Math.sin(a), 0]);
  const horiz = Math.hypot(fwd[0], fwd[1]);
  if (horiz < 0.05) return null;
  const yaw = heading - Math.atan2(fwd[0], fwd[1]) / D2R;
  fwd = rotZ(fwd, yaw);
  up = rotZ(up, yaw);
  right = rotZ(right, yaw);
  const pitch = Math.atan2(fwd[2], horiz) / D2R;
  const roll = Math.abs(up[2]) + Math.abs(right[2]) > 1e-9 ? Math.atan2(right[2], up[2]) / D2R : 0;
  return { right, up, fwd, pitch, roll, heading: norm360(heading) };
}

export function focalPx(longEdgePx: number, longEdgeFovDeg: number): number {
  return longEdgePx / 2 / Math.tan((longEdgeFovDeg * D2R) / 2);
}

function toCamera(v: Vec3, b: CameraBasis): Vec3 {
  const { right: r, up: u, fwd: f } = b;
  return [v[0] * r[0] + v[1] * r[1] + v[2] * r[2], v[0] * u[0] + v[1] * u[1] + v[2] * u[2], v[0] * f[0] + v[1] * f[1] + v[2] * f[2]];
}

export interface View {
  basis: CameraBasis;
  focal: number;
  width: number;
  height: number;
}

/** Screen point of an ENU point relative to the device fix; null if behind. */
export function projectPoint(e: number, n: number, u: number, v: View): [number, number] | null {
  const c = toCamera([e, n, u - CAMERA_HEIGHT_M], v.basis);
  if (c[2] < NEAR_PLANE_M) return null;
  return [v.width / 2 + (v.focal * c[0]) / c[2], v.height / 2 - (v.focal * c[1]) / c[2]];
}

/** A ground polyline as visible screen runs, clipped at the near plane. */
export function projectPolyline(path: [number, number][], v: View): [number, number][][] {
  const cam = path.map((p) => toCamera([p[0], p[1], -CAMERA_HEIGHT_M], v.basis));
  const scr = (c: Vec3): [number, number] => [v.width / 2 + (v.focal * c[0]) / c[2], v.height / 2 - (v.focal * c[1]) / c[2]];
  const runs: [number, number][][] = [];
  let run: [number, number][] = [];
  cam.forEach((c, i) => {
    const cin = c[2] >= NEAR_PLANE_M;
    if (i > 0) {
      const p = cam[i - 1];
      const pin = p[2] >= NEAR_PLANE_M;
      if (pin !== cin) {
        const t = (NEAR_PLANE_M - p[2]) / (c[2] - p[2]);
        const cut = scr([p[0] + t * (c[0] - p[0]), p[1] + t * (c[1] - p[1]), NEAR_PLANE_M]);
        if (pin) {
          run.push(cut);
          runs.push(run);
          run = [];
        } else run = [cut];
      }
    }
    if (cin) run.push(scr(c));
  });
  if (run.length >= 2) runs.push(run);
  return runs.filter((r) => r.length >= 2);
}

export interface Ray {
  azimuth_deg: number;
  depression_deg: number;
  ground_range_m: number | null;
}

/** The ray through a screen point (for tap-to-calibrate). */
export function unproject(sx: number, sy: number, v: View): Ray {
  const xc = (sx - v.width / 2) / v.focal, yc = (v.height / 2 - sy) / v.focal;
  const { right: r, up: u, fwd: f } = v.basis;
  const d: Vec3 = [xc * r[0] + yc * u[0] + f[0], xc * r[1] + yc * u[1] + f[1], xc * r[2] + yc * u[2] + f[2]];
  const horiz = Math.hypot(d[0], d[1]);
  return {
    azimuth_deg: enuBearing(d[0], d[1]),
    depression_deg: Math.atan2(-d[2], horiz) / D2R,
    ground_range_m: d[2] < -1e-6 ? (CAMERA_HEIGHT_M * horiz) / -d[2] : null,
  };
}

/** Half-width (ground METRES) of the band an asset could be in: GPS and
 * compass errors combined in quadrature (independent errors). */
export function lateralUncertaintyM(distance: number, gpsAccuracy: number | null, compassErrDeg: number | null): number {
  const gps = Math.max(gpsAccuracy ?? 0, 0);
  const ce = Math.max(compassErrDeg ?? DEFAULT_COMPASS_ERR_DEG, MIN_COMPASS_ERR_DEG);
  return Math.hypot(gps, Math.max(distance, 0) * Math.tan(ce * D2R));
}

/** GPS term of the band: a lid calibration that moved the position removes most of it. */
export function effectiveGpsM(gpsAccuracy: number | null, calibratedPosition: boolean): number {
  const gps = Math.max(gpsAccuracy ?? 0, 0);
  return calibratedPosition ? Math.min(gps, Math.max(CAL_RESIDUAL_GPS_M, 0.25 * gps)) : gps;
}

export function compassErrorDeg(reported: number | null): number {
  if (reported === null || !Number.isFinite(reported)) return DEFAULT_COMPASS_ERR_DEG;
  return Math.max(reported, MIN_COMPASS_ERR_DEG);
}

export interface DrawLimits {
  max_range_m: number;
  fade_start_m: number;
  min_depression_deg: number;
  band_max_m: number;
}

export function maxDrawRangeM(cameraHeight = CAMERA_HEIGHT_M, maxM = AR_DRAW_MAX_M, minDepDeg = MIN_DEPRESSION_DEG): number {
  return Math.min(maxM, cameraHeight / Math.tan(minDepDeg * D2R));
}

/** Opacity of something on the ground at this range (1 near, 0 at the cut-off). */
export function groundFade(distance: number, far: number, fadeStart = AR_FADE_START_M): number {
  if (distance >= far) return 0;
  if (distance <= fadeStart) return 1;
  return (far - distance) / (far - fadeStart);
}

/** The parts of a ground polyline (relative to the device) within `radius`. */
export function clipPathToDisc(path: [number, number][], radius: number): [number, number][][] {
  const runs: [number, number][][] = [];
  let run: [number, number][] = [];
  const r2 = radius * radius;
  if (path.length === 1) return path[0][0] ** 2 + path[0][1] ** 2 <= r2 ? [[path[0]]] : [];
  for (let i = 0; i + 1 < path.length; i++) {
    const [ax, ay] = path[i], [bx, by] = path[i + 1];
    const dx = bx - ax, dy = by - ay;
    const a = dx * dx + dy * dy, b = 2 * (ax * dx + ay * dy), c = ax * ax + ay * ay - r2;
    if (a === 0) continue;
    const disc = b * b - 4 * a * c;
    if (disc <= 0) {
      if (run.length) runs.push(run);
      run = [];
      continue;
    }
    const sq = Math.sqrt(disc);
    const t0 = Math.max(0, (-b - sq) / (2 * a)), t1 = Math.min(1, (-b + sq) / (2 * a));
    if (t0 >= t1) {
      if (run.length) runs.push(run);
      run = [];
      continue;
    }
    const p0: [number, number] = [ax + t0 * dx, ay + t0 * dy];
    const p1: [number, number] = [ax + t1 * dx, ay + t1 * dy];
    if (run.length && t0 === 0) run.push(p1);
    else {
      if (run.length) runs.push(run);
      run = [p0, p1];
    }
    if (t1 < 1) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length) runs.push(run);
  return runs.filter((r) => r.length >= 2);
}

/** Extra vertices every `step` metres (each piece fades by its own range). */
export function subdivide(run: [number, number][], step = 2): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < run.length; i++) {
    const [ax, ay] = run[i], [bx, by] = run[i + 1];
    const n = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / step));
    for (let k = 0; k < n; k++) out.push([ax + (k / n) * (bx - ax), ay + (k / n) * (by - ay)]);
  }
  out.push(run[run.length - 1]);
  return out;
}

/** The two ground lines +/- halfWidth either side of a run (a band with true
 * perspective: wide where the pipe runs away, thin where it crosses). */
export function corridorEdges(run: [number, number][], halfWidth: number, bandMax = BAND_MAX_M): [[number, number][], [number, number][]] {
  const w = Math.max(0, Math.min(halfWidth, bandMax));
  const n = run.length;
  const left: [number, number][] = [], right: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    let nx = 0, ny = 0;
    for (const j of [i - 1, i]) {
      if (j >= 0 && j < n - 1) {
        const dx = run[j + 1][0] - run[j][0], dy = run[j + 1][1] - run[j][1];
        const L = Math.hypot(dx, dy) || 1;
        nx += -dy / L;
        ny += dx / L;
      }
    }
    const L = Math.hypot(nx, ny) || 1;
    nx /= L;
    ny /= L;
    left.push([run[i][0] + w * nx, run[i][1] + w * ny]);
    right.push([run[i][0] - w * nx, run[i][1] - w * ny]);
  }
  return [left, right];
}

/** projectPoint for a GROUND point with the near-horizon cut (null beyond `far`). */
export function projectGroundPoint(e: number, n: number, v: View, far: number): [number, number] | null {
  if (Math.hypot(e, n) > far) return null;
  return projectPoint(e, n, 0, v);
}

/** Network from a water type ("wastewater" BEFORE "water": both contain it). */
export function networkOf(waterType: string | null | undefined): "potable" | "wastewater" | "stormwater" | "unknown" {
  const t = String(waterType ?? "").trim().toLowerCase().replace(/_/g, " ");
  if (!t || t === "nan" || t === "none") return "unknown";
  if (t.includes("waste") || t.includes("sewer") || t === "ww") return "wastewater";
  if (t.includes("storm") || t.includes("drain") || t === "sw") return "stormwater";
  if (t.includes("water") || t.includes("potable") || t.includes("drinking") || t === "ws") return "potable";
  return "unknown";
}

export interface CalCandidateIn {
  asset_id: string;
  asset_type: string;
  kind: string;
  e?: number;
  n?: number;
}

const CAL_TYPES: Record<string, string[]> = {
  any: ["meter", "toby", "valve", "hydrant", "backflow", "fitting", "other"],
  meter: ["meter", "toby"], valve: ["valve", "backflow"], hydrant: ["hydrant"], lid: ["other"],
};
const CAL_PRIOR: Record<string, number> = { meter: 1, toby: 1, valve: 1, hydrant: 1, other: 0.95, backflow: 0.9, fitting: 0.6 };

/** OFFLINE FALLBACK ONLY of field_ar.calibration_candidates (online, the
 * server ranks). Positions relative to the device. */
export function rankCalibrationCandidates<T extends CalCandidateIn>(assets: T[], tapAzimuth: number, tapRange: number | null,
  gpsAccuracy: number | null, compassErr: number | null, kind: string | null = null, limit = 5):
  { a: T; e: number; n: number; distance_m: number; angle_off_deg: number; score: number }[] {
  const types = new Set(CAL_TYPES[kind ?? "any"] ?? CAL_TYPES.any);
  const ce = Math.max(compassErrorDeg(compassErr), 8);
  const gps = Math.max(gpsAccuracy ?? 8, 3);
  const rngOk = tapRange !== null && Number.isFinite(tapRange) && tapRange > 0 && tapRange <= 60;
  const out: { a: T; e: number; n: number; distance_m: number; angle_off_deg: number; score: number }[] = [];
  for (const a of assets) {
    if (a.kind !== "point" || !types.has(a.asset_type) || a.e === undefined || a.n === undefined) continue;
    const d = Math.hypot(a.e, a.n);
    if (d > 45) continue;
    const da = angleDiff(enuBearing(a.e, a.n), tapAzimuth);
    if (Math.abs(da) > CALIBRATION_MAX_HEADING_DEG) continue;
    let s = Math.exp(-0.5 * (da / ce) ** 2);
    if (rngOk) {
      const dep = Math.atan2(CAMERA_HEIGHT_M, tapRange as number);
      const sigR = gps + (CAMERA_HEIGHT_M / Math.max(Math.sin(dep) ** 2, 1e-3)) * D2R;
      s *= Math.exp(-0.5 * ((d - (tapRange as number)) / sigR) ** 2);
    }
    s *= CAL_PRIOR[a.asset_type] ?? 0.8;
    out.push({ a, e: a.e, n: a.n, distance_m: d, angle_off_deg: da, score: s });
  }
  out.sort((p, q) => q.score - p.score);
  return out.slice(0, limit);
}

export interface Calibration {
  heading_offset_deg: number;
  east_m: number;
  north_m: number;
  range_shift_m: number;
  accepted: boolean;
  reason?: string;
  asset_id?: string;
  /** "position": the position moves, the heading is untouched (2026-10-05 default for a lid tap) */
  mode?: "position" | "full";
  clamped?: boolean;
}

/** OFFLINE FALLBACK ONLY - online, POST /api/field/ar-calibrate computes
 * this on the server (field_ar.calibration_offset, which documents the two
 * modes). "position": the ray in the CURRENT pose, heading untouched, the
 * device moved by (asset - tap point). */
export function calibrationOffset(assetE: number, assetN: number, tapAzimuth: number, tapRange: number | null, gpsAccuracy: number | null,
  mode: "position" | "full" = "full"): Calibration {
  const bearing = enuBearing(assetE, assetN);
  const dist = Math.hypot(assetE, assetN);
  const dh = angleDiff(bearing, tapAzimuth);
  const cap = Math.max(gpsAccuracy ?? 10, 0) + 5;
  const rngOk = tapRange !== null && Number.isFinite(tapRange) && tapRange > 0;
  if (mode === "position") {
    const a = tapAzimuth * D2R;
    const ue = Math.sin(a), un = Math.cos(a);
    let de: number, dn: number;
    if (rngOk) {
      de = assetE - (tapRange as number) * ue;
      dn = assetN - (tapRange as number) * un;
    } else {
      const along = assetE * ue + assetN * un;
      if (along <= 0) {
        return { heading_offset_deg: 0, east_m: 0, north_m: 0, range_shift_m: 0, accepted: false, mode,
          reason: "That asset is behind the direction you tapped. Pick the asset you tapped." };
      }
      de = assetE - along * ue;
      dn = assetN - along * un;
    }
    const move = Math.hypot(de, dn);
    const clamped = move > cap;
    if (clamped) {
      de *= cap / move;
      dn *= cap / move;
    }
    return { heading_offset_deg: 0, east_m: de, north_m: dn, range_shift_m: Math.hypot(de, dn), accepted: true, mode, clamped };
  }
  if (Math.abs(dh) > CALIBRATION_MAX_HEADING_DEG) {
    return { heading_offset_deg: 0, east_m: 0, north_m: 0, range_shift_m: 0, accepted: false, reason: `The tap is ${Math.round(Math.abs(dh))}° away from that asset. Pick the asset you tapped.` };
  }
  const out: Calibration = { heading_offset_deg: dh, east_m: 0, north_m: 0, range_shift_m: 0, accepted: true, mode: "full" };
  if (rngOk) {
    const shift = Math.max(-cap, Math.min(cap, dist - (tapRange as number)));
    out.clamped = Math.abs(dist - (tapRange as number)) > cap;
    out.range_shift_m = shift;
    out.east_m = shift * Math.sin(bearing * D2R);
    out.north_m = shift * Math.cos(bearing * D2R);
  }
  return out;
}

export function nearestOnPath(path: [number, number][]): [number, number] {
  if (path.length === 1) return path[0];
  let best = Infinity;
  let bq: [number, number] = path[0];
  for (let i = 0; i + 1 < path.length; i++) {
    const [ax, ay] = path[i], [bx, by] = path[i + 1];
    const dx = bx - ax, dy = by - ay;
    const L = dx * dx + dy * dy;
    const t = L === 0 ? 0 : Math.max(0, Math.min(1, (-ax * dx - ay * dy) / L));
    const q: [number, number] = [ax + t * dx, ay + t * dy];
    const d = Math.hypot(q[0], q[1]);
    if (d < best) {
      best = d;
      bq = q;
    }
  }
  return bq;
}

// ---------------------------------------------------------------------------
// Direct-manipulation calibration (2026-10-06): hold-and-drag the overlay,
// twist it with two fingers, or drag two assets onto their real spots.
// Mirrored in field_ar.py (ground_point_at, rotate_en, solve_rigid_2d,
// direct_calibration) - tests/test_field_ar_gestures.py and
// frontend/tests/arDirect.test.ts check the same cases.
//
// Frames. p = a map point relative to the device (ENU metres, the asset
// register as drawn with no extra correction); q = where the user says it
// really is, relative to the device, measured through the CURRENT pose
// (the calibration in force applied). The correction (rot, t) satisfies
//   p = R(rot) q + t
// where R(rot) turns a bearing clockwise by rot degrees. Adding rot to the
// heading offset and moving the device position by t (east, north) then
// draws p exactly at q.
// ---------------------------------------------------------------------------

/** the farthest ground point a drag may use (near the horizon a pixel is metres) */
export const DRAG_MAX_RANGE_M = 30;

/** The ground point (east, north from the device) under a screen point, or
 * null when the ray misses the ground or meets it beyond `maxRange`. */
export function groundPointAt(sx: number, sy: number, v: View, maxRange = DRAG_MAX_RANGE_M): [number, number] | null {
  const r = unproject(sx, sy, v);
  if (r.ground_range_m === null || !Number.isFinite(r.ground_range_m) || r.ground_range_m > maxRange) return null;
  const a = r.azimuth_deg * D2R;
  return [r.ground_range_m * Math.sin(a), r.ground_range_m * Math.cos(a)];
}

/** Turn (east, north) clockwise by `deg` (a bearing increases by deg). */
export function rotateEN(p: [number, number], deg: number): [number, number] {
  const r = deg * D2R;
  const c = Math.cos(r), s = Math.sin(r);
  return [p[0] * c + p[1] * s, -p[0] * s + p[1] * c];
}

export interface Pair {
  /** map point, relative to the device */
  p: [number, number];
  /** where it really is, relative to the device (current pose) */
  q: [number, number];
}

export interface RigidSolution {
  rot_deg: number;
  t: [number, number];
  /** rms of |R q + t - p| over the pairs, metres */
  residual_m: number;
  /** the largest |R q + t - p| */
  max_residual_m: number;
  /** the largest distance between two q points (how well the turn is pinned) */
  span_m: number;
}

/** Least-squares rigid transform (rotation + translation, NO scale) on the
 * ground: p ~ R(rot) q + t. `fixedRot`: keep the rotation (a position-only
 * drag) and solve the translation alone. One pair can only give a
 * translation (rot = fixedRot ?? 0). */
export function solveRigid2D(pairs: Pair[], fixedRot: number | null = null): RigidSolution {
  const n = pairs.length;
  if (!n) return { rot_deg: fixedRot ?? 0, t: [0, 0], residual_m: 0, max_residual_m: 0, span_m: 0 };
  let pe = 0, pn = 0, qe = 0, qn = 0;
  for (const { p, q } of pairs) {
    pe += p[0];
    pn += p[1];
    qe += q[0];
    qn += q[1];
  }
  pe /= n;
  pn /= n;
  qe /= n;
  qn /= n;
  let rot = fixedRot ?? 0;
  if (fixedRot === null && n >= 2) {
    // maximise sum p'.R q' where p.Rq = c (pe qe + pn qn) + s (pe qn - pn qe)
    let sc = 0, ss = 0;
    for (const { p, q } of pairs) {
      const ae = p[0] - pe, an = p[1] - pn, be = q[0] - qe, bn = q[1] - qn;
      sc += ae * be + an * bn;
      ss += ae * bn - an * be;
    }
    rot = Math.abs(sc) + Math.abs(ss) > 1e-12 ? Math.atan2(ss, sc) / D2R : 0;
  }
  const rq = rotateEN([qe, qn], rot);
  const t: [number, number] = [pe - rq[0], pn - rq[1]];
  let s2 = 0, mx = 0, span = 0;
  for (const { p, q } of pairs) {
    const r = rotateEN(q, rot);
    const d = Math.hypot(r[0] + t[0] - p[0], r[1] + t[1] - p[1]);
    s2 += d * d;
    mx = Math.max(mx, d);
  }
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) span = Math.max(span, Math.hypot(pairs[i].q[0] - pairs[j].q[0], pairs[i].q[1] - pairs[j].q[1]));
  }
  return { rot_deg: angleDiff(rot, 0), t, residual_m: Math.sqrt(s2 / n), max_residual_m: mx, span_m: span };
}

/** The two correspondences a two-finger twist of `twistDeg` (clockwise on
 * screen) about the ground point `pivot` (relative to the device, current
 * pose) stands for, given the correction already previewed (`rot`, `t`):
 * the map point shown at the pivot stays there, and the one shown 5 m east
 * of it turns about it. Feed them to solveRigid2D. Twisting the overlay
 * clockwise lowers the heading offset by the twist. */
export function twistPairs(pivot: [number, number], twistDeg: number, rot = 0, t: [number, number] = [0, 0]): Pair[] {
  const shown = (q: [number, number]): [number, number] => {
    const r = rotateEN(q, rot);
    return [r[0] + t[0], r[1] + t[1]];
  };
  const u: [number, number] = [5, 0];
  const ru = rotateEN(u, twistDeg);
  return [
    { p: shown(pivot), q: pivot },
    { p: shown([pivot[0] + u[0], pivot[1] + u[1]]), q: [pivot[0] + ru[0], pivot[1] + ru[1]] },
  ];
}
